import { Button } from '@lastbrand/web-ui/button'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'

import { BUTTON_TYPES } from '@src/quince-components/src/atoms/button'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import type Product from '@src/quince-site-common/src/domains/Product'
import BaseProduct from '@src/quince-site-common/src/domains/Product/BaseProduct'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import type { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { viewProductDetailsContainerCss, viewProductDetailsContentCss } from './product-cta-ds.styles'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'
import type { ReactNode, Ref } from 'react'

interface ProductDisplayButtonDSProps {
  children?: ReactNode
  onClick: () => void
  /**
   * Panda `css.raw` style object(s) merged onto the web-ui Button `css` prop (e.g. express-pay
   * sizing, waitlisted background). The web-ui Button takes `css`, not `className`, so all
   * style overrides flow through here.
   */
  buttonCss?: SystemStyleObject
  /** Legacy `showLoader` -> web-ui Button `loading`. */
  showLoader?: boolean
  /** Legacy `selectorKey` -> `data-selector-key` (the legacy Button emitted this attribute). */
  selectorKey?: string
  /** Legacy `buttonRef` -> web-ui Button `ref`. */
  buttonRef?: Ref<HTMLButtonElement>
  disabled?: boolean
  /** Legacy `buttonType` (PRIMARY/SECONDARY) -> web-ui Button `variant`. */
  buttonType?: BUTTON_TYPES
  showNewPdpDesignV2?: boolean
  /**
   * Cart-popup wiring, forwarded verbatim from legacy `ProductDisplayButton`. When the button is
   * rendered inside the cart popup (`isFromCartPopup`) on V2 desktop, a "View full product details"
   * link is shown; clicking it closes the cart popup via `setIsCartPopUpOpen(false)`.
   */
  product?: Product
  selectedOptions?: ProductVariantOption[]
  setIsCartPopUpOpen?: (isCartOpen: boolean) => void
  isFromCartPopup?: boolean
  /** Legacy `isBuyNowPdp` — threaded for parity with legacy `ProductDisplayButton`. */
  isBuyNowPdp?: boolean
}

// Legacy `.displayButtonWrapper { width: 100% }` — the CTA button is always full-bleed in its column.
// V2 (`.displayButtonWrapper_newDesignV2`, the shipped path) pins line-height 1.8rem (18px) and
// letter-spacing 0.07rem → 0.7px at the legacy ~10px root. The web-ui `size="lg"` recipe gives 20px
// line-height and no letter-spacing; use px here so it matches and doesn't re-inflate under the DS 16px
// root. Height stays the recipe default (no rem height per the spec).
const baseButtonCss: SystemStyleObject = { width: 'full', lineHeight: '18px', letterSpacing: '0.7px' }

/**
 * DS twin of legacy `ProductDisplayButton`. Composes the pill CTA from `@lastbrand/web-ui/button`.
 *
 * Prop mapping vs legacy:
 *   showLoader   -> loading
 *   buttonRef    -> ref
 *   selectorKey  -> data-selector-key (the legacy Button rendered this attribute internally)
 *   buttonType   -> variant (BUTTON_TYPES.PRIMARY/SECONDARY share the web-ui variant strings)
 *   round + size -> the web-ui Button recipe default (legacy used `round size=LARGE`); we use
 *                   `size="lg"` and the recipe's default radius — NOT overridden per the token spec.
 *
 * The legacy `ProductDisplayButton` also renders a "View full product details" link in the
 * cart-popup V2 desktop case (closes the cart popup on click). That wiring is reproduced here so
 * the DS button is a drop-in for the legacy button on the cart-popup surface.
 */
export const ProductDisplayButtonDS = ({
  children,
  onClick,
  buttonCss,
  showLoader,
  selectorKey,
  buttonRef,
  disabled = false,
  buttonType = BUTTON_TYPES.PRIMARY,
  showNewPdpDesignV2,
  product,
  selectedOptions,
  setIsCartPopUpOpen,
  isFromCartPopup,
}: ProductDisplayButtonDSProps) => {
  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  // eslint-disable-next-line i18next/no-literal-string -- web-ui Button variant tokens, not user copy
  const variant = buttonType === BUTTON_TYPES.SECONDARY ? 'secondary' : 'primary'
  return (
    <>
      {isFromCartPopup && showNewPdpDesignV2 && !isMobile && product ? (
        <div className={viewProductDetailsContainerCss}>
          <span className={viewProductDetailsContentCss}>
            <Link
              href={getProductLink(product as BaseProduct | Bundle, selectedOptions as ProductVariantOption[])}
              onClick={() => {
                setIsCartPopUpOpen?.(false)
              }}
            >
              {t('view_full_product_details')}
            </Link>
          </span>
        </div>
      ) : null}
      <Button
        ref={buttonRef}
        variant={variant}
        size="lg"
        loading={showLoader}
        disabled={disabled}
        onClick={onClick}
        css={{ ...baseButtonCss, ...buttonCss }}
        data-selector-key={selectorKey}
      >
        {children}
      </Button>
    </>
  )
}
